<template>
    <div class="admin-instance-page">
        <div class="nk-block-head nk-block-head-sm">
            <div class="nk-block-between align-items-start">
                <div class="nk-block-head-content admin-instance-list__intro">
                    <div class="admin-instance-list__title-row">
                        <h3 class="nk-block-title page-title mb-0">{{ trans('blog.blog') }}</h3>
                    </div>
                    <div class="nk-block-des text-soft" v-if="!posts.total">
                        <p>{{ trans('blog.module_info_description') }}</p>
                    </div>
                </div>
                <div class="nk-fmg-actions flex-shrink-0">
                    <ul class="nk-block-tools g-2 flex-wrap justify-content-end">
                        <li>
                            <a href="#" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--outline" @click.prevent="showFilterPanel = !showFilterPanel">
                                <admin-icon name="sliders"></admin-icon> <span>{{ trans('general.filter') }}</span>
                            </a>
                        </li>
                        <li class="nk-block-tools-opt d-sm-block">
                            <button type="button" class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid" v-if="hasPermission('create-blog')" v-b-modal.modal-blog>
                                <admin-icon name="plus"></admin-icon> {{ trans('general.add_new') }}
                            </button>
                        </li>
                    </ul>
                </div>
            </div>
        </div>

        <transition name="fade">
            <div class="card admin-instance-form-card mb-2" v-if="showFilterPanel">
                <div class="card-body">
                    <div class="row">
                        <div class="col-6 col-md-4">
                            <div class="form-group">
                                <label>{{ trans('blog.title') }}</label>
                                <input class="form-control" v-model="filterPostForm.title">
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="form-group">
                                <label>{{ trans('blog.status') }}</label>
                                <select class="form-control" v-model="filterPostForm.status">
                                    <option value="">{{ trans('general.select_one') }}</option>
                                    <option value="draft">{{ trans('blog.draft') }}</option>
                                    <option value="published">{{ trans('blog.published') }}</option>
                                </select>
                            </div>
                        </div>
                        <div class="col-6 col-md-4">
                            <div class="form-group">
                                <label>{{ trans('general.order') }}</label>
                                <select class="form-control" v-model="filterPostForm.order">
                                    <option value="asc">{{ trans('general.ascending') }}</option>
                                    <option value="desc">{{ trans('general.descending') }}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </transition>

        <div class="admin-list-table-card mt-2" v-if="hasPermission('list-blog') && posts.total">
            <div class="table-responsive">
                <table class="table admin-data-table admin-data-table--config-list mb-0">
                    <thead>
                        <tr>
                            <sortable-th field="title" :sort-by="filterPostForm.sort_by" :order="filterPostForm.order" @sort="sortList">{{ trans('blog.title') }}</sortable-th>
                            <th>{{ trans('blog.category') }}</th>
                            <sortable-th field="status" :sort-by="filterPostForm.sort_by" :order="filterPostForm.order" @sort="sortList">{{ trans('blog.status') }}</sortable-th>
                            <sortable-th field="published_at" :sort-by="filterPostForm.sort_by" :order="filterPostForm.order" @sort="sortList">{{ trans('blog.published_at') }}</sortable-th>
                            <th class="admin-table-actions-cell">{{ trans('general.actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="post in posts.data" :key="post.id">
                            <td>
                                <div class="d-flex flex-column">
                                    <span v-text="post.title"></span>
                                    <span class="text-soft">{{ post.slug }}</span>
                                </div>
                            </td>
                            <td>{{ post.category || '-' }}</td>
                            <td>
                                <span class="admin-status-badge" :class="post.status === 'published' ? 'admin-status-badge--success' : 'admin-status-badge--warning'">
                                    {{ trans('blog.' + post.status) }}
                                </span>
                            </td>
                            <td>{{ post.published_at | moment }}</td>
                            <td class="admin-table-actions-cell">
                                <table-row-action-dropdown>
                                    <b-dropdown-item-button v-if="hasPermission('edit-blog')" @click="editPost(post)">
                                        <admin-icon name="pencil"></admin-icon> {{ trans('general.edit') }}
                                    </b-dropdown-item-button>
                                    <b-dropdown-item-button
                                        v-if="hasPermission('delete-blog')"
                                        v-confirm="{ok: confirmDelete(post)}"
                                        class="admin-row-actions__danger"
                                    >
                                        <admin-icon name="trash"></admin-icon> {{ trans('general.delete') }}
                                    </b-dropdown-item-button>
                                </table-row-action-dropdown>
                            </td>
                        </tr>
                    </tbody>
                </table>
            </div>
            <div class="admin-table-pagination-wrap">
                <pagination-record :page-length.sync="filterPostForm.page_length" :records="posts" @updateRecords="getPosts" @change.native="getPosts"></pagination-record>
            </div>
        </div>

        <div class="admin-list-table-card admin-empty-state mt-2" v-if="!posts.total" role="status">
            <div class="admin-empty-state__inner">
                <div class="admin-empty-state__icon-wrap" aria-hidden="true">
                    <admin-icon name="file"></admin-icon>
                </div>
                <h4 class="admin-empty-state__title">{{ trans('blog.module_info_title') }}</h4>
                <p class="admin-empty-state__text">{{ trans('blog.module_info_description') }}</p>
                <button
                    v-if="hasPermission('create-blog')"
                    type="button"
                    class="btn btn-sm admin-toolbar-btn admin-toolbar-btn--solid mt-2"
                    v-b-modal.modal-blog
                >
                    <admin-icon name="plus"></admin-icon> {{ trans('general.add_new') }}
                </button>
            </div>
        </div>

        <b-modal id="modal-blog" hide-footer centered size="lg" modal-class="admin-modal-pro" content-class="admin-modal-pro__content">
            <template #modal-title>{{ trans('blog.add_new_post') }}</template>
            <blog-form @completed="getPosts"></blog-form>
        </b-modal>
    </div>
</template>
<script>
    import blogForm from './form'

    export default {
        components: { blogForm },
        data() {
            return {
                posts: { total: 0, data: [] },
                filterPostForm: {
                    title: '',
                    status: '',
                    sort_by: 'created_at',
                    order: 'desc',
                    page_length: helper.getConfig('page_length')
                },
                showFilterPanel: false
            };
        },
        mounted() {
            this.getPosts();
        },
        methods: {
            hasPermission(permission) {
                return helper.hasPermission(permission);
            },
            getPosts(page) {
                if (typeof page !== 'number') {
                    page = 1;
                }
                this.$bvModal.hide('modal-blog');
                let url = helper.getFilterURL(this.filterPostForm);
                axios.get('/api/blog?page=' + page + url)
                    .then(response => response.data)
                    .then(response => this.posts = response)
                    .catch(error => helper.showDataErrorMsg(error));
            },
            editPost(post) {
                this.$router.push('/blog/' + post.id + '/edit');
            },
            confirmDelete(post) {
                return dialog => this.deletePost(post);
            },
            deletePost(post) {
                axios.delete('/api/blog/' + post.id)
                    .then(response => response.data)
                    .then(response => {
                        toastr.success(response.message);
                        this.getPosts();
                    }).catch(error => helper.showDataErrorMsg(error));
            },
            sortList(field) {
                if (this.filterPostForm.sort_by === field) {
                    this.filterPostForm.order = this.filterPostForm.order === 'asc' ? 'desc' : 'asc';
                } else {
                    this.filterPostForm.sort_by = field;
                    this.filterPostForm.order = 'asc';
                }
            }
        },
        filters: {
            moment(date) {
                return date ? helper.formatDateTime(date) : '-';
            }
        },
        watch: {
            filterPostForm: {
                handler() { this.getPosts(); },
                deep: true
            }
        }
    }
</script>
